import { useContext } from "react"
import { ModalType } from "../types"
import { ModalContext } from "../../ModalsContext"

export const useModals = () => {
  const context = useContext(ModalContext)
  if (!context) {
    throw new Error("useModals must be used within a ModalProvider")
  }

  const {
    modals,
    openModal,
    closeModal,
    getModalParams,
    getModalParamsRequired
  } = context

  const isModalOpen = (type: ModalType): boolean => modals[type] || false
  const hasAnyModalOpen = (): boolean =>
    Object.values(modals).some((isOpen) => isOpen)
  const getOpenModals = (): ModalType[] => {
    return Object.entries(modals)
      .filter(([_, isOpen]) => isOpen)
      .map(([type, _]) => type as ModalType)
  }

  const closeAllModals = () => {
    const openModals = getOpenModals()
    openModals.forEach((modalType) => {
      if (!["globalError", "pageLoadingError"].includes(modalType)) {
        closeModal(modalType)
      }
    })
  }

  return {
    modals,
    openModal,
    closeModal,
    getModalParams,
    getModalParamsRequired,
    isModalOpen,
    hasAnyModalOpen,
    getOpenModals,
    closeAllModals
  }
}

export const useModalParams = <T extends ModalType>(type: T) => {
  const { getModalParams } = useModals()
  return getModalParams(type)
}

export const useIsModalOpen = (type: ModalType) => {
  const { isModalOpen } = useModals()
  return isModalOpen(type)
}
